import { createEffect, sample } from 'effector';
import { t } from 'i18n';
import $$api from 'models/api';
import $$giftTrust from 'models/gift-trusts';
import { showModal } from 'models/modals';
import $$giftTrustPage from 'models/pages/gift-trust';
import { navigateFx } from 'models/router-gate';
import { Fragment, createElement } from 'react';

import DocumentDownloadService from 'services/DocumentDownloadService';
import GiftTrustService from 'services/GiftTrustService';

import { type RegisteredModals } from 'types/Modals';
import { type Answers } from 'types/zod/answers';
import { type GiftTrustItem } from 'types/zod/gift-trust-item';
import { type QuestionnaireInstance } from 'types/zod/questionnaire-instance';

// Mirror $$giftTrust.$qInstances to $$giftTrustPage.$qInstances
sample({
    source: $$giftTrust.$qInstances,
    target: $$giftTrustPage.$qInstances,
});

// When create gift trust button is clicked, use $$giftTrust model to create new instance
sample({
    clock: $$giftTrustPage.createGiftTrustButtonClicked,
    target: $$giftTrust.createNewInstanceFx,
});

// When add gift button is clicked, use $$giftTrust model to create new instance
sample({
    clock: $$giftTrustPage.addGiftButtonClicked,
    target: $$giftTrustPage.openGiftItemModal,
});

// When delete gift button is clicked, use $$giftTrust model to create new instance
sample({
    clock: $$giftTrustPage.deleteGiftButtonClicked,
    target: $$giftTrust.removeGiftItem,
});

// When generate gift button is clicked, use $$giftTrust model to generate gift item
sample({
    clock: $$giftTrustPage.generateGiftButtonClicked,
    target: $$giftTrust.generateGiftItemFx,
});

// When openGiftItemModal event is fired, open Add Gift Item modal
sample({
    clock: $$giftTrustPage.openGiftItemModal,
    filter: Boolean,
    fn: (qInstanceId) => ({ modalName: 'ADD_GIFT_ITEM_MODAL' as RegisteredModals, state: { qInstanceId } }),
    target: showModal,
});

// When openGiftItemModal event is fired, open Add Gift Item modal
sample({
    clock: $$giftTrustPage.openGiftItemModal,
    filter: Boolean,
    fn: (qInstanceId) => ({ modalName: 'ADD_GIFT_ITEM_MODAL' as RegisteredModals, state: { qInstanceId } }),
    target: showModal,
});

// $isSigned is a map of qInstanceId to boolean, where boolean is true if the document has Executed document_status_code
sample({
    source: {
        documents: $$giftTrust.$documents,
        qInstances: $$giftTrust.$qInstances,
        answers: $$giftTrust.$answers,
        giftTrustItems: $$giftTrust.$giftTrustItems,
        isPayed: $$giftTrust.$isPayed,
        jobs: $$giftTrust.$jobs,
    },
    fn: ({ documents, qInstances, answers, giftTrustItems, isPayed, jobs }) => {
        return qInstances.reduce(
            (acc, qInstance) => {
                const qInstanceId = qInstance.questionnaire_instance_id;
                const giftTrustDocuments = documents[qInstanceId].filter(
                    (document) => document.questionnaire_instance_id === qInstanceId,
                );
                return {
                    ...acc,
                    [qInstanceId]: {
                        documents: giftTrustDocuments,
                        isSigned: GiftTrustService.getIsSigned(answers[qInstanceId] || {}),
                        answers: answers[qInstanceId] || null,
                        giftTrustItems: giftTrustItems[qInstanceId] || [],
                        qInstance,
                        isPayed: isPayed[qInstanceId],
                        name: GiftTrustService.getGiftTrustName(answers[qInstanceId] || {}),
                        jobs: jobs.filter((job) => job.qInstanceId === qInstanceId),
                    },
                };
            },
            {} as Record<
                QuestionnaireInstance['questionnaire_instance_id'],
                {
                    isSigned: boolean;
                    answers: Answers | null;
                    giftTrustItems: GiftTrustItem[];
                    qInstance: QuestionnaireInstance;
                    isPayed: boolean;
                    name: string;
                    jobs: {
                        qInstanceId: QuestionnaireInstance['questionnaire_instance_id'];
                        giftTrustItemId: GiftTrustItem['id'];
                        job_id?: number;
                    }[];
                }
            >,
        );
    },
    target: $$giftTrustPage.$tabs,
});

// Handle resumeButtonClicked
sample({
    source: sample({
        source: $$giftTrust.$qInstances,
        clock: $$giftTrustPage.resumeButtonClicked,
        fn: (qInstances, qInstanceId) =>
            qInstances.find((qInstance) => qInstance.questionnaire_instance_id === qInstanceId),
    }),
    filter: Boolean,
    fn: (qInstance) =>
        `/document/${qInstance?.questionnaire_type_code}/${qInstance?.state_code}/${qInstance?.questionnaire_instance_id}/continue`,
    target: navigateFx,
});

// Handle deleteButtonClicked
sample({
    clock: $$giftTrustPage.deleteButtonClicked,
    source: $$giftTrust.$isPayed,
    filter: (isPayed, qInstanceId) => !isPayed[qInstanceId],
    fn: (_isPayed, qInstanceId) => ({
        modalName: 'MODALITY' as const,
        state: {
            title: t('giftTrustPage.deleteQInstanceModelity.title'),
            body: t('giftTrustPage.deleteQInstanceModelity.body'),
            options: {
                confirmButton: t('giftTrustPage.deleteQInstanceModelity.confirmButton'),
                closeButton: t('giftTrustPage.deleteQInstanceModelity.closeButton'),
                onConfirm: async () => {
                    await $$api.qInstances.deleteInstanceFx({ id: qInstanceId });
                    // await EstatePlanService.loadEstatePlan();
                },
            },
        },
    }),
    target: showModal,
});

// Handle downloadAllButtonClicked
sample({
    source: sample({
        source: $$giftTrust.$documents,
        clock: $$giftTrustPage.downloadAllButtonClicked,
        fn: (documents, qInstanceId) => documents?.[qInstanceId]?.[0]?.document_id,
    }),
    filter: Boolean,
    target: createEffect(DocumentDownloadService.downloadZip),
});

// Handle documentsButtonClicked
sample({
    clock: $$giftTrustPage.documentsButtonClicked,
    source: $$giftTrust.$documents,
    fn: (documents, qInstanceId) => ({
        modalName: 'GIFT_TRUST_DOCUMENTS_MODAL' as RegisteredModals,
        state: {
            qInstanceId: qInstanceId,
            documents: documents?.[qInstanceId],
        },
    }),
    target: showModal,
});

// Handle updateButtonClicked
sample({
    source: $$giftTrust.$qInstances,
    clock: $$giftTrustPage.updateButtonClicked,
    fn: (qInstances, qInstanceId) => ({
        modalName: 'MODALITY' as const,
        state: {
            title: t('EPCard.updatePlan'),
            body: createElement(
                Fragment,
                null,
                t('EPCard.updateConfirmation') as string,
                createElement('div', { className: 'miro-code-label' }, 'U'),
            ),
            options: {
                confirmButton: t('EPCard.updateYes'),
                closeButton: t('EPCard.updateNo'),
                onConfirm: () => {
                    const qInstance = qInstances.find(
                        (qInstance) => qInstance.questionnaire_instance_id === qInstanceId,
                    );
                    if (qInstance) {
                        navigateFx(
                            `/document/${qInstance?.questionnaire_type_code}/${qInstance?.state_code}/${qInstance?.questionnaire_instance_id}`,
                        );
                    }
                },
            },
        },
    }),
    target: showModal,
});

// Handle markAsSignedClicked
// sample({
//     source: $$giftTrust.$answers,
//     clock: $$giftTrustPage.markAsSignedClicked,
//     fn: (answers, qInstanceId) => ({
//         qInstanceId,
//         answers: GiftTrustService.setIsSigned({ answers: answers[qInstanceId], isSigned: true }),
//     }),
//     target: $$giftTrust.setIsSignedFx,
// });
sample({
    source: $$giftTrust.$answers,
    clock: $$giftTrustPage.markAsSignedClicked,
    fn: (answers, qInstanceId) => ({
        modalName: 'MODALITY' as const,
        state: {
            title: t('giftTrustPage.markAsSignedModelity.title'),
            body: createElement(
                Fragment,
                null,
                t('giftTrustPage.markAsSignedModelity.body') as string,
                createElement('div', { className: 'miro-code-label' }, 'U'),
            ),
            options: {
                confirmButton: t('giftTrustPage.markAsSignedModelity.confirmButton'),
                closeButton: t('giftTrustPage.markAsSignedModelity.closeButton'),
                onConfirm: async () => {
                    await $$giftTrust.setIsSignedFx({
                        qInstanceId,
                        answers: GiftTrustService.setIsSigned(answers[qInstanceId]),
                    });
                },
            },
        },
    }),
    target: showModal,
});
